import { heapTrackProperties } from '@m/utils/heap';

import type { ContactInfoDisplay, ContactInfoSubmission } from './contactInfoDisplay';
import type { ContactSaveFailureReason } from './contactInfoErrors';
import type { OnboardingVariant } from './deriveVariant';

export type OnboardingAnalyticsStep =
  'ContactInfo' | 'Payment' | 'Phone' | 'ReviewSubscription' | 'Success' | 'Vehicle' | 'VerifyCode';

const FLOW_LABEL: Record<OnboardingVariant, string> = {
  agnostic: 'Agnostic Sign-up',
  enterpriseSub: 'Enterprise Sign-up',
  individualSub: 'Individual Sub Sign-up',
  locationSpecific: 'Start Visit',
};

function track(
  action: 'Completed' | 'SaveFailed' | 'Skipped' | 'Viewed',
  step: OnboardingAnalyticsStep,
  variant: OnboardingVariant,
  optional: boolean,
  props: Record<string, boolean | string> = {},
) {
  heapTrackProperties(`SignInOnboarding:${step}:${action}`, {
    ...props,
    flow: FLOW_LABEL[variant],
    optional,
  });
}

export function trackOnboardingStepViewed(
  step: OnboardingAnalyticsStep,
  variant: OnboardingVariant,
  optional = false,
) {
  track('Viewed', step, variant, optional);
}

export function trackOnboardingStepCompleted(
  step: OnboardingAnalyticsStep,
  variant: OnboardingVariant,
  optional = false,
) {
  track('Completed', step, variant, optional);
}

export function trackOnboardingStepSkipped(
  step: OnboardingAnalyticsStep,
  variant: OnboardingVariant,
  optional = true,
) {
  track('Skipped', step, variant, optional);
}

/** `display` (fields shown, optional badge, prefills) is on every ContactInfo event so each one can
 *  be segmented by A/B arm. */
export function trackContactInfoViewed(
  variant: OnboardingVariant,
  optional: boolean,
  display: ContactInfoDisplay,
) {
  track('Viewed', 'ContactInfo', variant, optional, display);
}

export function trackContactInfoSkipped(
  variant: OnboardingVariant,
  optional: boolean,
  display: ContactInfoDisplay,
) {
  track('Skipped', 'ContactInfo', variant, optional, display);
}

/**
 * `Completed` fires when any shown contact field has a value on submit, including values
 * pre-filled from the account or invite. `addedEmail` / `addedName` say which fields were
 * submitted; `emailTyped` / `nameTyped` say which the customer entered or changed themselves.
 */
export function trackContactInfoCompleted(
  variant: OnboardingVariant,
  optional: boolean,
  props: ContactInfoDisplay & ContactInfoSubmission,
) {
  track('Completed', 'ContactInfo', variant, optional, props);
}

/** Saving the contact step failed; the customer stays on the step and can retry. */
export function trackContactInfoSaveFailed(
  variant: OnboardingVariant,
  optional: boolean,
  props: ContactInfoDisplay & { reason: ContactSaveFailureReason },
) {
  track('SaveFailed', 'ContactInfo', variant, optional, props);
}
